import { Injectable } from '@angular/core';
import { Observable, map, of, catchError } from 'rxjs';
import { FirmService } from '@6grain/masofa-monolith-api-client/dictionaries';
import { UserService, RoleService } from '@6grain/masofa-monolith-api-client/identity';
import { buildFilterQuery } from '@app/utils/_msf';

export interface User {
  id: string;
  username: string;
  email: string;
  createdDate: string;
  createdDateObj?: Date | null;
  role: string;
  roles?: string[];
  period: string;
  lockoutStart?: Date | null;
  lockoutEnd?: Date | null;
  status: 'active' | 'blocked';
  selected?: boolean;
  organizationId?: string;
  originalData?: any;
}

export interface RoleWithUsers {
  roleName: any;
  users: any[];
}

@Injectable()
export class UserManagementService {
  constructor(private userService: UserService, private firmService: FirmService, private roleService: RoleService) {}

  getUsers(filters: any, pageSize: number = 1000, offset: number = 0): Observable<{ users: User[]; total: number }> {
    let reqFilterParams: any = { ...filters };
    let normalisedRoleName: string | undefined = undefined;

    if (reqFilterParams.period) {
      if (reqFilterParams.period[0]) reqFilterParams.startDate = reqFilterParams.period[0];
      if (reqFilterParams.period[1]) reqFilterParams.endDate = reqFilterParams.period[1];
      delete reqFilterParams.period;
    }
    if (reqFilterParams.role) {
      normalisedRoleName = reqFilterParams.role.toLowerCase();
      delete reqFilterParams.role;
    }

    reqFilterParams = buildFilterQuery(reqFilterParams);
    const requestParams = { take: pageSize, offset, filters: reqFilterParams };

    return this.userService.identityUserCustomGetListPost(requestParams).pipe(
      map((response: any) => {
        if (!response || !Array.isArray(response)) return { users: [], total: 0 };
        let allUsers = response.map((item: any) => this.mapCustomResponseToModel(item));
        if (normalisedRoleName) {
          allUsers = allUsers.filter(
            (user: any) => user.roles && user.roles.some((role: string) => role.toLowerCase() === normalisedRoleName)
          );
        }
        return { users: allUsers, total: allUsers.length };
      })
    );
  }

  getCustomUserById(id: string): Observable<any> {
    return this.userService.identityUserCustomGetByIdIdGet(id);
  }

  private mapCustomResponseToModel(item: any): User {
    const base = item.baseInfo || item;
    const profile = item.profileData || {};

    const lockoutStart = this.parseLockoutDate(base.lockoutStart ?? base.scheduledLockoutStart);
    const lockoutEnd = this.parseLockoutDate(base.lockoutEnd ?? base.scheduledLockoutEnd);

    return {
      id: base.id || '',
      username: base.userName || '',
      email: base.email || '',
      createdDate: base.createAt
        ? new Date(base.createAt).toLocaleDateString('ru-RU')
        : base.createdDate
        ? new Date(base.createdDate).toLocaleDateString('ru-RU')
        : '',
      createdDateObj: base.createAt ? new Date(base.createAt) : null,
      role: base.roles && base.roles.length > 0 ? base.roles[0] : '',
      roles: base.roles || [],
      period: this.formatLockPeriod(lockoutStart, lockoutEnd),
      lockoutStart,
      lockoutEnd,
      status: base.status || (base.isActive ? 'active' : 'blocked'),
      selected: false,
      organizationId: base.userBusinessId || '',
      originalData: { ...base, ...profile },
    };
  }

  getUserFilters(): Observable<any[]> {
    return this.userService.identityUserGetUserFiltersGet();
  }

  getRoles(): Observable<any[]> {
    return this.roleService.identityRoleGetRolesGet();
  }

  getRolesWithUsers(): Observable<RoleWithUsers[]> {
    return this.roleService.identityRoleGetRolesWithUsersGet() as unknown as Observable<RoleWithUsers[]>;
  }

  getUsersInRole(roleName: string): Observable<any[]> {
    return this.roleService.identityRoleGetUsersInRoleGet(roleName);
  }

  getOrganizations(): Observable<any[]> {
    return this.firmService.dictionariesFirmGetByQueryPost({}).pipe(
      map((response: any) =>
        (response || []).map((org: any) => ({
          ...org,
          id: org.id ? String(org.id) : org.id,
          shortName: org.internationalName || org.shortName || org.fullName || '',
          fullName: org.fullName || org.shortName || '',
        }))
      ),
      catchError(() => of([]))
    );
  }

  private parseLockoutDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatLockPeriod(start: Date | null, end: Date | null): string {
    if (start && end) return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    if (start) return this.formatDate(start);
    if (end) return this.formatDate(end);
    return '';
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  getUsersForSelector(params: { offset: number; take: number; filters: any[] }): Observable<any[]> {
    const searchFilter = params.filters.find((f) => f.filterField === 'fullName');
    const searchTerm = searchFilter ? searchFilter.filterValue : '';

    const apiFilters = [];
    if (searchTerm) {
      apiFilters.push({
        filterField: 'fullName',
        filterOperator: 'contains',
        filterValue: searchTerm,
      });
    }

    const requestParams: any = {
      take: params.take,
      offset: params.offset,
      filters: apiFilters,
    };

    return this.userService.identityUserCustomGetListPost(requestParams).pipe(
      map((response: any) => {
        if (!response || !Array.isArray(response)) return [];

        return response.map((item: any) => {
          const base = item.baseInfo || item;
          const derivedFullName = [base.lastName, base.firstName, base.secondName].filter(Boolean).join(' ');

          return {
            id: base.id,
            fullName: derivedFullName || base.userName || base.email,
          };
        });
      })
    );
  }

  getUsersTotalCount(params: { filters: any[] }): Observable<number> {
    const searchFilter = params.filters.find((f) => f.filterField === 'fullName');
    const searchTerm = searchFilter ? searchFilter.filterValue : '';
    const apiFilters = [];
    if (searchTerm) {
      apiFilters.push({
        filterField: 'fullName',
        filterOperator: 'contains',
        filterValue: searchTerm,
      });
    }
    params.filters.forEach((f) => {
      if (f.filterField !== 'fullName') {
        apiFilters.push(f);
      }
    });
    const requestParams: any = {
      take: 0,
      offset: 0,
      filters: apiFilters,
    };
    return this.userService.identityUserCustomGetTotalCountPost(requestParams).pipe(
      catchError((err) => {
        console.error('Error getting total count', err);
        return of(0);
      })
    );
  }

  getUserById(id: string): Observable<any> {
    return this.getCustomUserById(id).pipe(
      map((res: any) => ({
        id: res.id,
        fullName: `${res.lastName} ${res.firstName} ${res.secondName || ''}`.trim() || res.userName,
      }))
    );
  }

  getOrganizationsForSelector(params: { offset: number; take: number; filters: any[] }): Observable<any[]> {
    const searchFilter = params.filters.find((f) => f.filterField === 'shortName');
    const searchTerm = searchFilter ? searchFilter.filterValue : null;

    const requestBody: any = {
      paging: { skip: params.offset, take: params.take },
      filters: [],
    };

    if (searchTerm) {
      requestBody.filters.push({
        filterField: 'shortName',
        filterOperator: 'contains',
        filterValue: searchTerm,
      });
    }

    return this.firmService
      .dictionariesFirmCustomGetByQueryPost(requestBody)
      .pipe(map((response: any) => response || []));
  }

  getOrganizationsTotalCount(params: { filters: any[] }): Observable<number> {
    const searchFilter = params.filters.find((f) => f.filterField === 'shortName');
    const searchTerm = searchFilter ? searchFilter.filterValue : null;

    const requestBody: any = {
      paging: { skip: 0, take: 0 },
      filters: [],
    };

    if (searchTerm) {
      requestBody.filters.push({
        filterField: 'shortName',
        filterOperator: 'contains',
        filterValue: searchTerm,
      });
    }

    return this.firmService.dictionariesFirmCustomGetTotalCountPost(requestBody);
  }

  getOrganizationById(id: string): Observable<any> {
    return this.firmService.dictionariesFirmGetByIdIdGet(id);
  }

  getRolesForSelector(params: { offset: number; take: number; filters: any[] }): Observable<any[]> {
    return this.getRoles().pipe(
      map((roles) => {
        const safeRoles = roles || [];
        const searchFilter = params.filters.find((f) => f.filterField === 'normalizedName');
        let filtered = safeRoles;
        if (searchFilter && searchFilter.filterValue) {
          const val = searchFilter.filterValue.toLowerCase();
          filtered = safeRoles.filter((r) => {
            const matchesBaseName =
              (r.name && r.name.toLowerCase().includes(val)) ||
              (r.normalizedName && r.normalizedName.toLowerCase().includes(val));
            let matchesLocalizedName = false;
            if (r.names && typeof r.names === 'object') {
              matchesLocalizedName = Object.values(r.names).some((nameValue: any) =>
                String(nameValue).toLowerCase().includes(val)
              );
            }
            return matchesBaseName || matchesLocalizedName;
          });
        }
        return filtered.slice(params.offset, params.offset + params.take);
      })
    );
  }
}
